<script setup>
import { i18n } from 'boot/i18n';
import { useDialogPluginComponent } from 'quasar';
import {
  confirmDialog,
  HrAdaptiveFormDialog,
  HrConfirmBtn,
  HrDismissBtn,
  HrErrorBanner,
  HrListSelect,
  HrMultiSelect,
  HrSubtleBtn,
  HrTextField,
  HrWarningBanner,
  notifyNegative,
} from 'src/design-system';
import { computed, ref } from 'vue';

import {
  LEARNING_SCOPE_OPTIONS,
  LEARNING_STATUSES,
  MIN_LEARNING_LENGTH,
} from '../../domain/constants/auditLearnings';
import {
  isStaleLearningErrorCode,
  isUnsafeLearningErrorCode,
  toLearningRejectionReason,
} from '../../domain/constants/auditLearningErrorCodes';
import { AIUnavailableError } from '../../domain/errors/AIUnavailableError';
import { useAuditLearnings } from '../composables/useAuditLearnings';
import { useLearningsAnalytics } from '../composables/useLearningsAnalytics';

/** @typedef {import('../../domain/entities/AuditLearning.types').AuditLearning} AuditLearning */

defineOptions({ name: 'EditLearningDialog' });

const props = defineProps({
  clinicId: {
    type: String,
    required: true,
  },
  /** The learning being edited, or null when creating one. */
  learningRecord: {
    type: Object,
    default: null,
  },
  /** The active learning a draft revision replaces, shown for reference while editing that revision. */
  parentLearning: {
    type: Object,
    default: null,
  },
  /** @type {import('vue').PropType<Array<{ id: string, noteTemplateName: string }>>} */
  sessionNoteTemplates: {
    type: Array,
    default: () => [],
  },
  learningEditAttemptId: {
    type: String,
    default: null,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();
const {
  isProcessing,
  abandonSaves,
  createDraftRevision,
  createLearning,
  saveLearning,
} = useAuditLearnings();

const {
  LEARNING_EDIT_MODES,
  LEARNING_SAVE_INTENTS,
  LEARNING_STATUS_CHANGE_CAUSES,
  LEARNING_STATUSES: ANALYTICS_STATUSES,
  LEARNING_VALIDATION_OUTCOMES,
  describeScope,
  describeScopeChange,
  describeStatus,
  draftAgeInMinutes,
  trackEditAbandoned,
  trackLearningSaved,
  trackRevisionRestored,
  trackStatusChanged,
  trackValidationCompleted,
} = useLearningsAnalytics();

const isEditing = computed(() => !!props.learningRecord);
const isDraft = computed(() => props.learningRecord?.status === LEARNING_STATUSES.DRAFT);
const isRevision = computed(() => isDraft.value && !!props.learningRecord?.parentId);

function normalizeLearning(value) {
  return value?.trim?.() ?? '';
}

const learningText = ref(props.learningRecord?.learning ?? '');

/*
 * A learning that names no template applies to the whole organization, so an empty list is what
 * pre-selects that option. That also covers a learning the thumbs-down flow scoped to one activity type:
 * the column already reads "Organization" for it, and saving here is what makes that true of the stored
 * record as well.
 */
const initialScope = (() => {
  if (!props.learningRecord) {
    return LEARNING_SCOPE_OPTIONS.TEMPLATES;
  }

  return props.learningRecord.sessionNoteTemplateIds?.length
    ? LEARNING_SCOPE_OPTIONS.TEMPLATES
    : LEARNING_SCOPE_OPTIONS.ORGANIZATION;
})();

const scope = ref(initialScope);
const selectedTemplateIds = ref([...(props.learningRecord?.sessionNoteTemplateIds ?? [])]);

const isVerifying = ref(false);
// A draft save runs no safety check, so it gets no banner — but it is still a round trip, and without a
// spinner on its own button nothing on screen says the save is happening.
const isSavingDraft = ref(false);
const rejectionReason = ref(null);

/*
 * Any save in flight, not only a verified one. Both kinds capture their payload before the request goes
 * out, so the fields have to be locked for a draft save too — otherwise an edit made while it runs is
 * silently dropped and the dialog closes reporting success. The component's own two flags are included
 * rather than leaning on the composable's alone, so the lock does not depend on when that one is set.
 */
const isSaving = computed(() => isProcessing.value || isVerifying.value || isSavingDraft.value);

const scopeOptions = computed(() => [
  {
    label: i18n.t('noteAuditor.learningsModeration.editLearningDialog.scopeTemplates'),
    value: LEARNING_SCOPE_OPTIONS.TEMPLATES,
  },
  {
    label: i18n.t('noteAuditor.learningsModeration.editLearningDialog.scopeOrganization'),
    value: LEARNING_SCOPE_OPTIONS.ORGANIZATION,
  },
]);

const templateOptions = computed(() => props.sessionNoteTemplates.map(({ id, noteTemplateName }) => ({
  label: noteTemplateName,
  value: id,
})));

const isTemplateScope = computed(() => scope.value === LEARNING_SCOPE_OPTIONS.TEMPLATES);

const templatesFieldLabel = computed(() => (
  selectedTemplateIds.value.length > 0
    ? i18n.t('noteAuditor.learningsModeration.editLearningDialog.templatesLabelSelected')
    : i18n.t('noteAuditor.learningsModeration.editLearningDialog.templatesLabelEmpty')
));

const sessionNoteTemplateIds = computed(() => (
  isTemplateScope.value ? [...selectedTemplateIds.value] : []
));

const normalizedLearning = computed(() => normalizeLearning(learningText.value));

const characterCount = computed(() => normalizedLearning.value.length);

const counterText = computed(() => (
  i18n.t('noteAuditor.learningsModeration.editLearningDialog.counter', {
    count: characterCount.value,
    min: MIN_LEARNING_LENGTH,
  })
));

// An untouched field is not a mistake yet, so the counter only turns red once there is too little text
// rather than no text.
const isCounterShort = computed(() => (
  characterCount.value > 0 && characterCount.value < MIN_LEARNING_LENGTH
));

/*
 * Validation is held in this component rather than delegated to field `rules`, for two reasons the rules
 * cannot give us. Nothing may go red until the manager has actually tried to save — a length rule firing
 * on the first keystroke reads as being told off for typing — and when they do try, every unfinished field
 * has to go red at once, where a form stops at the first failure unless it is greedy.
 */
const hasAttemptedSave = ref(false);
const validationFailureCount = ref(0);
const restoreUsed = ref(false);
const hasSaved = ref(false);

const analyticsMode = computed(() => (
  isEditing.value ? LEARNING_EDIT_MODES.EDIT : LEARNING_EDIT_MODES.CREATE
));

const initialTemplateIds = [...(props.learningRecord?.sessionNoteTemplateIds ?? [])];
const initialLearningText = normalizeLearning(props.learningRecord?.learning ?? '');

const hasChanges = computed(() => (
  hasAttemptedSave.value
  || restoreUsed.value
  || normalizedLearning.value !== initialLearningText
  || !!describeScopeChange(initialTemplateIds, sessionNoteTemplateIds.value)
));

/**
 * @returns {Object}
 */
function analyticsContext() {
  return {
    learningEditAttemptId: props.learningEditAttemptId,
    mode: analyticsMode.value,
    learningId: props.learningRecord?.id ?? null,
  };
}

const templatesError = computed(() => (
  isTemplateScope.value && selectedTemplateIds.value.length === 0
    ? i18n.t('noteAuditor.learningsModeration.editLearningDialog.validationTemplatesRequired')
    : null
));

const learningError = computed(() => {
  if (!normalizedLearning.value) {
    return i18n.t('common.validationRequired');
  }

  return characterCount.value < MIN_LEARNING_LENGTH
    ? i18n.t('common.validationMinLength', { min: MIN_LEARNING_LENGTH })
    : null;
});

const showTemplatesError = computed(() => hasAttemptedSave.value && !!templatesError.value);
const showLearningError = computed(() => hasAttemptedSave.value && !!learningError.value);

const title = computed(() => {
  if (!isEditing.value) {
    return i18n.t('noteAuditor.learningsModeration.editLearningDialog.titleCreate');
  }

  return isDraft.value
    ? i18n.t('noteAuditor.learningsModeration.editLearningDialog.titleEditDraft')
    : i18n.t('noteAuditor.learningsModeration.editLearningDialog.titleEdit');
});

const rejectionHeading = computed(() => (
  isEditing.value
    ? i18n.t('noteAuditor.learningsModeration.editLearningDialog.rejectedHeadingUpdate')
    : i18n.t('noteAuditor.learningsModeration.editLearningDialog.rejectedHeadingCreate')
));

const primaryLabel = computed(() => {
  if (isVerifying.value) {
    return i18n.t('noteAuditor.learningsModeration.editLearningDialog.buttonVerifying');
  }

  if (rejectionReason.value) {
    return i18n.t('noteAuditor.learningsModeration.editLearningDialog.buttonVerifyAgain');
  }

  return isRevision.value
    ? i18n.t('noteAuditor.learningsModeration.editLearningDialog.buttonVerifyAndReplace')
    : i18n.t('common.buttonSave');
});

const replacedLearningText = computed(() => (
  isRevision.value ? normalizeLearning(props.parentLearning?.learning) : ''
));

const canRestoreReplacedLearning = computed(() => (
  !!replacedLearningText.value && replacedLearningText.value !== normalizedLearning.value
));

/**
 * Any edit — to the wording or to what it applies to — invalidates the verdict the model gave on what was
 * screened before, so the rejection goes with it.
 */
function onEditInvalidatesVerdict() {
  rejectionReason.value = null;
}

function onScopeChanged() {
  onEditInvalidatesVerdict();

  if (!isTemplateScope.value) {
    selectedTemplateIds.value = [];
  }
}

/**
 * Pull the wording that is still applied to audits back into the field.
 */
function onRestoreReplacedLearning() {
  learningText.value = props.parentLearning?.learning ?? '';
  rejectionReason.value = null;
  restoreUsed.value = true;

  trackRevisionRestored({
    learningEditAttemptId: props.learningEditAttemptId,
    learningId: props.learningRecord?.id,
  });
}

/**
 * Map the failures we expect to their own wording, and let anything else fall through to the generic
 * message — a raw callable error names the backend's internals and is unactionable for the reader.
 *
 * @param {Error} err
 */
function reportSaveError(err) {
  if (isUnsafeLearningErrorCode(err?.details?.code)) {
    rejectionReason.value = i18n.t('noteAuditor.feedback.notifyLearningUnsafe');
    validationFailureCount.value += 1;

    trackValidationCompleted({
      ...analyticsContext(),
      outcome: LEARNING_VALIDATION_OUTCOMES.FAILED,
      failureReason: toLearningRejectionReason(err?.details),
      attemptNumber: validationFailureCount.value,
    });

    return;
  }

  console.error('Failed to save learning', err);

  if (err instanceof AIUnavailableError) {
    notifyNegative(i18n.t('common.errors.aiUnavailable'));

    return;
  }

  notifyNegative(i18n.t(isStaleLearningErrorCode(err?.code)
    ? 'noteAuditor.learningsModeration.notifications.learningStale'
    : 'common.errors.generic'));
}

/**
 * @param {Object} params
 * @param {string} params.saveIntent
 * @param {boolean} [params.revisionCreated]
 * @param {boolean} [params.revisionPromoted]
 * @returns {void}
 */
function reportSaved({ saveIntent, revisionCreated = false, revisionPromoted = false }) {
  hasSaved.value = true;

  const templateIds = sessionNoteTemplateIds.value;
  const scopeChange = isEditing.value
    ? describeScopeChange(initialTemplateIds, templateIds)
    : null;

  if (saveIntent === LEARNING_SAVE_INTENTS.VERIFIED) {
    trackValidationCompleted({
      ...analyticsContext(),
      outcome: LEARNING_VALIDATION_OUTCOMES.PASSED,
      attemptNumber: validationFailureCount.value + 1,
    });
  }

  trackLearningSaved({
    ...analyticsContext(),
    saveIntent,
    scope: describeScope(templateIds),
    templateCount: templateIds.length,
    source: props.learningRecord?.source ?? null,
    previousStatus: props.learningRecord ? describeStatus(props.learningRecord) : null,
    revisionCreated,
    revisionPromoted,
    scopeChanged: !!scopeChange,
    scopeChange,
    restoreUsed: restoreUsed.value,
    validationFailureCount: validationFailureCount.value,
  });

  const wasDraft = props.learningRecord?.status === LEARNING_STATUSES.DRAFT;

  if (saveIntent === LEARNING_SAVE_INTENTS.VERIFIED && wasDraft) {
    trackStatusChanged({
      learningId: props.learningRecord.id,
      fromStatus: describeStatus(props.learningRecord),
      toStatus: ANALYTICS_STATUSES.ACTIVE,
      cause: revisionPromoted
        ? LEARNING_STATUS_CHANGE_CAUSES.REVISION_PROMOTED
        : LEARNING_STATUS_CHANGE_CAUSES.VERIFIED_SAVE,
      draftAgeMinutes: draftAgeInMinutes(props.learningRecord),
      learningEditAttemptId: props.learningEditAttemptId,
    });
  }
} // reportSaved()

/**
 * Reveals the errors and reports whether the form may be saved. Called by both save actions, so a draft
 * is held to the same shape as a verified save — only the safety check differs.
 *
 * @returns {boolean}
 */
function isReadyToSave() {
  hasAttemptedSave.value = true;

  return !templatesError.value && !learningError.value;
}

/**
 * @param {boolean} saveAsDraft
 * @returns {Promise<{ isCurrent: boolean, promoted: boolean }>}
 */
async function persist(saveAsDraft) {
  const payload = {
    clinicId: props.clinicId,
    learning: normalizedLearning.value,
    sessionNoteTemplateIds: sessionNoteTemplateIds.value,
    saveAsDraft,
  };

  if (!isEditing.value) {
    const { isCurrent } = await createLearning(payload);

    return { isCurrent, promoted: false };
  }

  return saveLearning({ ...payload, learningId: props.learningRecord.id });
}

/**
 * Verified save. The safety check runs inside this call with the modal still open, so a rejection is
 * shown before anything is written and the version currently applied to audits is untouched.
 *
 * @returns {Promise<void>}
 */
async function onSubmitForm() {
  if (!isReadyToSave()) {
    return;
  }

  isVerifying.value = true;
  rejectionReason.value = null;

  // The catch covers the save only. Closing the dialog sits outside it, so a failure there cannot be
  // reported to the manager as a learning that was refused when it was in fact stored.
  let outcome;

  try {
    outcome = await persist(false);
  } catch (err) {
    reportSaveError(err);
    return;
  } finally {
    isVerifying.value = false;
  }

  if (!outcome.isCurrent) {
    return;
  }

  reportSaved({
    saveIntent: LEARNING_SAVE_INTENTS.VERIFIED,
    revisionPromoted: outcome.promoted,
  });

  onDialogOK({ created: !isEditing.value, promoted: outcome.promoted, savedAsDraft: false });
} // onSubmitForm()

/**
 * @returns {Promise<void>}
 */
async function saveDraftInPlace() {
  let outcome;

  isSavingDraft.value = true;

  try {
    outcome = await persist(true);
  } catch (err) {
    reportSaveError(err);
    return;
  } finally {
    isSavingDraft.value = false;
  }

  if (outcome.isCurrent) {
    reportSaved({ saveIntent: LEARNING_SAVE_INTENTS.DRAFT });

    onDialogOK({ created: !isEditing.value, promoted: false, savedAsDraft: true });
  }
} // saveDraftInPlace()

/**
 * @returns {Promise<void>}
 */
async function saveAsDraftRevision() {
  let outcome;

  isSavingDraft.value = true;

  try {
    outcome = await createDraftRevision({
      clinicId: props.clinicId,
      parentId: props.learningRecord.id,
      learning: normalizedLearning.value,
      sessionNoteTemplateIds: sessionNoteTemplateIds.value,
    });
  } catch (err) {
    reportSaveError(err);
    return;
  } finally {
    isSavingDraft.value = false;
  }

  if (outcome.isCurrent) {
    reportSaved({ saveIntent: LEARNING_SAVE_INTENTS.DRAFT, revisionCreated: true });

    onDialogOK({ created: false, promoted: false, savedAsDraft: true });
  }
} // saveAsDraftRevision()

/**
 * Parking an edit to an active learning creates a second row rather than changing the one in front of the
 * manager, so it is named before it happens.
 *
 * @returns {Promise<void>}
 */
async function onSaveAsDraft() {
  if (!isReadyToSave()) {
    return;
  }

  if (isEditing.value && !isDraft.value) {
    confirmDialog(
      i18n.t('noteAuditor.learningsModeration.dialogs.saveAsDraftTitle'),
      i18n.t('noteAuditor.learningsModeration.dialogs.saveAsDraftMessage'),
      saveAsDraftRevision,
      {
        confirmLabel: i18n.t('noteAuditor.learningsModeration.editLearningDialog.buttonSaveAsDraft'),
      },
    );

    return;
  }

  await saveDraftInPlace();
} // onSaveAsDraft()

function onHide() {
  abandonSaves();

  if (!hasSaved.value) {
    trackEditAbandoned({
      learningEditAttemptId: props.learningEditAttemptId,
      mode: analyticsMode.value,
      validationFailureCount: validationFailureCount.value,
      hadChanges: hasChanges.value,
    });
  }

  onDialogHide();
}

function onCancel() {
  onDialogCancel();
}
</script>

<template>
  <hr-adaptive-form-dialog
    ref="dialogRef"
    size="md"
    :title="title"
    @hide="onHide"
    @submit="onSubmitForm"
  >
    <hr-error-banner
      v-if="rejectionReason"
      class="q-mb-md"
      multi-line
      :heading="rejectionHeading"
      :message="rejectionReason"
    />

    <!--
      Locked while any save is in flight, so the payload that was captured is the payload that gets
      written. A draft save captures its values just as a verified one does, so leaving the fields open
      during it would accept edits the request has already passed over and then close as a success.
    -->
    <hr-list-select
      v-model="scope"
      class="q-mb-md"
      :options="scopeOptions"
      :disable="isSaving"
      :label="i18n.t('noteAuditor.learningsModeration.editLearningDialog.scopeLabel')"
      data-e2e="select-learning-scope"
      @update:model-value="onScopeChanged"
    />

    <hr-multi-select
      v-model="selectedTemplateIds"
      class="q-mb-md"
      :options="templateOptions"
      :disable="isSaving || !isTemplateScope"
      :error="showTemplatesError"
      :error-message="templatesError"
      :label="templatesFieldLabel"
      data-e2e="select-learning-templates"
      @update:model-value="onEditInvalidatesVerdict"
    />

    <hr-text-field
      v-model="learningText"
      :disable="isSaving"
      :error="showLearningError"
      :error-message="learningError"
      :label="i18n.t('noteAuditor.learningsModeration.editLearningDialog.label')"
      multiline
      outlined
      hide-bottom-space
      data-e2e="field-learning-description"
      @update:model-value="onEditInvalidatesVerdict"
    />

    <div class="learning-dialog__counter q-mt-xs">
      <span :class="{ 'learning-dialog__counter--short': isCounterShort }">
        {{ counterText }}
      </span>
    </div>

    <div
      v-if="isRevision"
      class="learning-replaced q-mt-sm q-pa-sm"
    >
      <div class="learning-replaced__label text-caption text-uppercase">
        {{ i18n.t('noteAuditor.learningsModeration.editLearningDialog.activeLearningReplacedLabel') }}
      </div>
      <div class="learning-replaced__text">
        {{ replacedLearningText }}
      </div>
      <hr-subtle-btn
        v-if="canRestoreReplacedLearning"
        :label="i18n.t('noteAuditor.learningsModeration.editLearningDialog.buttonRestore')"
        :disable="isProcessing"
        @click="onRestoreReplacedLearning"
      />
    </div>

    <hr-warning-banner
      v-if="isVerifying"
      class="q-mt-md"
      :message="i18n.t('noteAuditor.learningsModeration.editLearningDialog.verifying')"
    />

    <template #actions>
      <!-- Stays available while a save is in flight: backing out of a slow safety check is reasonable,
           and the abandoned result is dropped rather than painted over a closed dialog. -->
      <hr-dismiss-btn
        :label="i18n.t('common.buttonCancel')"
        @click="onCancel"
      />

      <!-- Neutral rather than primary: saving a draft is the quieter of the two ways out. `--_btn-color`
           is the hook HrBtn reads for exactly this, the same way HrHeader and HrBanner retint theirs. -->
      <hr-subtle-btn
        class="learning-dialog__draft-btn"
        :label="i18n.t('noteAuditor.learningsModeration.editLearningDialog.buttonSaveAsDraft')"
        :loading="isSavingDraft"
        :disable="isSaving"
        data-e2e="button-save-learning-draft"
        @click="onSaveAsDraft"
      />

      <hr-confirm-btn
        type="submit"
        :label="primaryLabel"
        :loading="isVerifying"
        :disable="isSaving"
        data-e2e="button-save-learning"
      />
    </template>
  </hr-adaptive-form-dialog>
</template>

<style scoped>
.learning-dialog__counter {
  display: flex;
  justify-content: flex-end;
  font-size: 12px;
  color: var(--app-on-surface-variant);
}

.learning-dialog__counter--short {
  color: var(--app-error);
}

.learning-dialog__draft-btn {
  --_btn-color: var(--app-on-surface-variant);
}

.learning-replaced {
  border-inline-start: 3px solid var(--app-outline-variant);
  background: var(--app-surface-container-low);
}

.learning-replaced__label {
  color: var(--app-on-surface-variant);
}

.learning-replaced__text {
  white-space: pre-wrap;
}
</style>
